<script setup lang="ts">
import type { Asset } from "@artech-global/types";
import { reactive, ref } from "vue";
import { useResponseError } from "~/composables/error";
import type { AdminUser } from "~~/schemas/admin-user";

const { $api } = useNuxtApp();
const { t } = useI18n();
const toast = useToast();
const authStore = useAuthStore();
const { formErrors, errorMsg, setErrors, clearErrors } = useResponseError();

const open = ref(false);
const saveLoading = ref(false);
const form = reactive({
  fullName: "",
  email: "",
  imageAssetID: "",
  imageAsset: null as Asset | null,
});

const avatarSrc = computed(() => form.imageAsset?.url || "");
const initials = computed(() => (form.fullName || form.email || "").substring(0, 2).toUpperCase());

function assign(user?: AdminUser | null) {
  form.fullName = user?.fullName || "";
  form.email = user?.email || "";
  form.imageAssetID = user?.imageAssetID || "";
  form.imageAsset = user?.imageAsset || null;
}

async function load() {
  const data = await $api<AdminUser>("/profile");
  authStore.updateUser(data);
  assign(data);
}

async function openModal() {
  clearErrors();
  assign(authStore.user);
  open.value = true;
  try {
    await load();
  } catch (e: any) {
    toast.error({ title: e?.data?.message || t("profile.loadError") });
  }
}

function close() {
  open.value = false;
  clearErrors();
  assign(authStore.user);
}

function onUploaded(asset: Asset) {
  form.imageAssetID = asset.id;
  form.imageAsset = asset;
}

function onUploadError({ message }: { file: File; message: string }) {
  toast.error({ title: t("profile.avatarUploadFailed"), description: message });
}

async function submit() {
  clearErrors();
  saveLoading.value = true;
  try {
    await $api("/profile", {
      method: "POST",
      body: {
        fullName: form.fullName,
        email: form.email,
        imageAssetID: form.imageAssetID,
      },
    });
    const data = await $api<AdminUser>("/profile");
    authStore.updateUser(data);
    assign(data);
    toast.success({ title: t("profile.saveSuccess") });
    open.value = false;
  } catch (e: any) {
    setErrors(e);
    if (errorMsg.value && Object.keys(formErrors.value).length === 0) {
      toast.error({ title: errorMsg.value });
    }
  } finally {
    saveLoading.value = false;
  }
}

defineExpose({
  open: openModal,
  close,
});
</script>

<template>
  <Modal
    :open="open"
    :title="t('profile.title')"
    size="sm"
    @update:open="(v: boolean) => (open = v)"
    @close="close"
  >
    <form class="flex flex-col gap-medium" autocomplete="off" @submit.prevent="submit">
      <div class="flex items-center gap-small">
        <Avatar :src="avatarSrc" :initials="initials" size="large" shape="square" />
        <AssetUpload
          v-model="form.imageAssetID"
          accept="image/*"
          type="button"
          file-list="none"
          :max-file-size="2 * 1024 * 1024"
          :disabled="saveLoading"
          :browse-button-text="t('profile.changeAvatar')"
          @uploaded="onUploaded"
          @error="onUploadError"
        />
      </div>

      <FormField
        v-model="form.fullName"
        :label="t('field.fullName')"
        :error="formErrors.fullName"
      />

      <FormField
        v-model="form.email"
        type="email"
        :label="t('field.email')"
        :disabled="true"
        :help-text="t('profile.emailLockedHelp')"
        :error="formErrors.email"
      />

    </form>

    <template #actions>
      <Button variant="tertiary" size="small" @click="close">
        {{ t("action.cancel") }}
      </Button>
      <Button
        variant="primary"
        size="small"
        :disabled="saveLoading"
        @click="submit"
      >
        <ProgressCircle v-if="saveLoading" size="small" />
        {{ t("action.save") }}
      </Button>
    </template>
  </Modal>
</template>
